// ignore-string-externalization

import React from 'react';
import styled from 'styled-components';
import { hexToRgba } from '../../../../../features/src/color-conversion';
import {
  screenSmMax,
  screenXsMax,
  screenSmMin,
  spacer8,
  spacer12,
  spacer16,
  spacer32,
  spacer40,
  spacer56,
  Type,
} from '@spotify-internal/encore-web';
import { IconArrowRight } from '@spotify-internal/encore-web-v6';
import {
  DELAYS,
  fadeInAnimation,
  withAnimationContext,
} from '@spotify-internal/frodor-modules';
import { formatText, fixHref } from '../../../utils';
import { ButtonTertiaryAsLink } from '../../../ui';
import { RelatedLinkType } from './RelatedLinkType';
import { ModuleType } from '../../types';

const RelatedLinkBox = styled.div`
  @media (max-width: ${screenXsMax}) {
    padding: ${spacer32} ${spacer12};
  }

  @media (min-width: ${screenSmMin}) {
    padding: ${spacer40} ${spacer40};
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: 'celloCanon',
  })`
    ${fadeInAnimation(DELAYS.FIRST)};
    padding-bottom: 0px;
  `,
);

const Subtext = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'ballad',
  })`
    ${fadeInAnimation(DELAYS.SECOND)};

    @media (max-width: ${screenSmMax}) {
      width: 100%;
    }
  `,
);

const Description = withAnimationContext(
  styled(Type.p).attrs({
    variant: 'viola',
  })`
    ${fadeInAnimation(DELAYS.THIRD)};
  `,
);

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
  padding-bottom: ${spacer56};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding-bottom: ${spacer8};
  }
`;

const LinkBox = styled.div`
  @media (max-width: ${screenSmMax}) {
    padding-top: 0px;
  }
`;

const InnerTextContainer = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  width: 65%;
  align-items: flex-end;

  @media (max-width: ${screenSmMax}) {
    align-items: flex-start;
    text-align: left;
    padding-bottom: ${spacer16};
  }
`;

const RelatedTextLink = withAnimationContext(
  styled(ButtonTertiaryAsLink)`
    ${fadeInAnimation(DELAYS.FOURTH)};
    color: ${({ theme }) => theme.box.text};

    &:hover:not(:focus):not(:disabled) {
      transform: none;
      color: ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
    }
  `,
);

const TRACK_CAT = 'module-relatedLinksModule';

export type RelatedLinkModuleType = {
  relatedLink: RelatedLinkType;
};

export const RelatedLink = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  RelatedLinkModuleType
>(({ relatedLink = {} }, ref) => {
  return (
    <RelatedLinkBox ref={ref as React.RefObject<HTMLDivElement>}>
      <TextContainer>
        <Headline>{relatedLink.headline}</Headline>
        <InnerTextContainer>
          <Subtext
            dangerouslySetInnerHTML={{
              __html: formatText(relatedLink.subtext),
            }}
          />
          <Description>{relatedLink.description}</Description>
        </InnerTextContainer>
      </TextContainer>
      <LinkBox>
        <RelatedTextLink
          href={fixHref(relatedLink.link)}
          track={[TRACK_CAT, `Link - ${relatedLink.linkText}`]}
          iconTrailing={() => <IconArrowRight iconSize={16} />}
          condensedAll
        >
          {relatedLink.linkText}
        </RelatedTextLink>
      </LinkBox>
    </RelatedLinkBox>
  );
});
